package fr.frux.showcase

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.frux.app.resources.Res
import fr.frux.app.resources.showcase_button_disabled
import fr.frux.app.resources.showcase_button_filled
import fr.frux.app.resources.showcase_button_loading
import fr.frux.app.resources.showcase_button_outline
import fr.frux.app.resources.showcase_button_text
import fr.frux.app.resources.showcase_button_tonal
import fr.frux.app.resources.showcase_chip_apples
import fr.frux.app.resources.showcase_chip_pears
import fr.frux.app.resources.showcase_chip_selected
import fr.frux.app.resources.showcase_chip_unselected
import fr.frux.app.resources.showcase_empty_message
import fr.frux.app.resources.showcase_empty_title
import fr.frux.app.resources.showcase_error_message
import fr.frux.app.resources.showcase_field_address
import fr.frux.app.resources.showcase_field_error_message
import fr.frux.app.resources.showcase_field_tree_name
import fr.frux.app.resources.showcase_section_badges
import fr.frux.app.resources.showcase_section_buttons
import fr.frux.app.resources.showcase_section_cards
import fr.frux.app.resources.showcase_section_chips
import fr.frux.app.resources.showcase_section_fields
import fr.frux.app.resources.showcase_section_season_strip
import fr.frux.app.resources.showcase_section_species_glyphs
import fr.frux.app.resources.showcase_section_states
import fr.frux.app.resources.showcase_title
import fr.frux.app.resources.showcase_tree_cherry_meta
import fr.frux.app.resources.showcase_tree_cherry_name
import fr.frux.app.resources.showcase_tree_favorite_meta
import fr.frux.app.resources.showcase_tree_favorite_name
import fr.frux.app.resources.showcase_tree_fig_meta
import fr.frux.app.resources.showcase_tree_fig_name
import fr.frux.app.resources.showcase_tree_park_meta
import fr.frux.app.resources.showcase_tree_park_name
import fr.frux.app.resources.showcase_tree_pear_meta
import fr.frux.app.resources.showcase_tree_pear_name
import fr.frux.app.resources.showcase_tree_walnut_meta
import fr.frux.app.resources.showcase_tree_walnut_name
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxFilterChip
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.SeasonStrip
import fr.frux.core.designsystem.components.SectionHeader
import fr.frux.core.designsystem.components.StatusBadge
import fr.frux.core.designsystem.components.TreeCard
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.rememberSpeciesGlyphPainters
import fr.frux.core.designsystem.interaction.clearFocusOnTap
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors
import org.jetbrains.compose.resources.stringResource

/**
 * Dev-only screen showcasing every component of the design system.
 * Not part of any real user flow — reachable via Route.Showcase for manual QA.
 */
@Composable
fun ShowcaseScreen(onBack: () -> Unit) {
    Scaffold(
        topBar = {
            FruxTopBar(title = stringResource(Res.string.showcase_title), onBack = onBack)
        },
        containerColor = MaterialTheme.colorScheme.background,
    ) { innerPadding ->
        LazyColumn(
            modifier =
                Modifier
                    .fillMaxSize()
                    .padding(innerPadding)
                    // Tap hors du champ actif -> ferme le clavier (#126), sans consommer le geste.
                    .clearFocusOnTap(),
            contentPadding = PaddingValues(FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        ) {
            item { ButtonsSection() }
            item { ChipsSection() }
            item { BadgesSection() }
            item { SeasonStripSection() }
            item { FieldsSection() }
            item { CardsSection() }
            item { StatesSection() }
            item { SpeciesGlyphsSection() }
        }
    }
}

@Composable
private fun ButtonsSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_buttons))
        FruxButton(
            text = stringResource(Res.string.showcase_button_filled),
            onClick = {},
            style = FruxButtonStyle.Filled,
        )
        FruxButton(text = stringResource(Res.string.showcase_button_tonal), onClick = {}, style = FruxButtonStyle.Tonal)
        FruxButton(
            text = stringResource(Res.string.showcase_button_outline),
            onClick = {},
            style = FruxButtonStyle.Outline,
        )
        FruxButton(text = stringResource(Res.string.showcase_button_text), onClick = {}, style = FruxButtonStyle.Text)
        FruxButton(
            text = stringResource(Res.string.showcase_button_loading),
            onClick = {},
            style = FruxButtonStyle.Filled,
            loading = true,
        )
        FruxButton(
            text = stringResource(Res.string.showcase_button_disabled),
            onClick = {},
            style = FruxButtonStyle.Filled,
            enabled = false,
        )
    }
}

@Composable
private fun ChipsSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_chips))
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            FruxFilterChip(
                label = stringResource(Res.string.showcase_chip_apples),
                selected = true,
                onClick = {},
                leadingEmoji = "🍎",
            )
            FruxFilterChip(
                label = stringResource(Res.string.showcase_chip_pears),
                selected = false,
                onClick = {},
                leadingEmoji = "🍐",
            )
        }
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            FruxFilterChip(label = stringResource(Res.string.showcase_chip_selected), selected = true, onClick = {})
            FruxFilterChip(label = stringResource(Res.string.showcase_chip_unselected), selected = false, onClick = {})
        }
    }
}

@Composable
private fun BadgesSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_badges))
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            FruxStatus.entries.forEach { status ->
                StatusBadge(status = status)
            }
        }
    }
}

@Composable
private fun SeasonStripSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_season_strip))
        SeasonStrip(
            inSeasonMonths = setOf(9, 10),
            soonMonths = setOf(8),
            currentMonth = 8,
        )
    }
}

@Composable
private fun FieldsSection() {
    var value by remember { mutableStateOf("") }
    var errorValue by remember { mutableStateOf("") }
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_fields))
        FruxTextField(
            value = value,
            onValueChange = { value = it },
            label = stringResource(Res.string.showcase_field_tree_name),
        )
        FruxTextField(
            value = errorValue,
            onValueChange = { errorValue = it },
            label = stringResource(Res.string.showcase_field_address),
            isError = true,
            supportingText = stringResource(Res.string.showcase_field_error_message),
        )
    }
}

@Composable
private fun CardsSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_cards))
        TreeCard(
            name = stringResource(Res.string.showcase_tree_park_name),
            meta = stringResource(Res.string.showcase_tree_park_meta),
            status = FruxStatus.Available,
            onClick = {},
            fruitEmoji = "🍏",
        )
        TreeCard(
            name = stringResource(Res.string.showcase_tree_cherry_name),
            meta = stringResource(Res.string.showcase_tree_cherry_meta),
            status = FruxStatus.Flowering,
            onClick = {},
            glyph = FruxSpeciesGlyphs.Cerise,
        )
        TreeCard(
            name = stringResource(Res.string.showcase_tree_walnut_name),
            meta = stringResource(Res.string.showcase_tree_walnut_meta),
            status = FruxStatus.Picked,
            onClick = {},
            glyph = FruxSpeciesGlyphs.Noix,
        )
        TreeCard(
            name = stringResource(Res.string.showcase_tree_fig_name),
            meta = stringResource(Res.string.showcase_tree_fig_meta),
            status = FruxStatus.Unavailable,
            onClick = {},
        )
        // Vignette photo (#223 lot B) : thumbUrl remplace la cascade emoji/glyphe/icone, avec
        // repli identique pendant le chargement (FruxRemoteImage).
        TreeCard(
            name = stringResource(Res.string.showcase_tree_pear_name),
            meta = stringResource(Res.string.showcase_tree_pear_meta),
            status = FruxStatus.Available,
            onClick = {},
            thumbUrl = "https://picsum.photos/seed/frux-poirier/200",
        )
        // Vignette #198 : coeur favori actif teinte accentCherry, a cote du glyphe espece.
        TreeCard(
            name = stringResource(Res.string.showcase_tree_favorite_name),
            meta = stringResource(Res.string.showcase_tree_favorite_meta),
            status = FruxStatus.Available,
            onClick = {},
            glyph = FruxSpeciesGlyphs.Pomme,
            trailing = {
                Icon(
                    imageVector = Icons.Filled.Favorite,
                    contentDescription = null,
                    tint = MaterialTheme.fruxColors.accentCherry,
                )
            },
        )
    }
}

@Composable
private fun StatesSection() {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_states))
        Box(modifier = Modifier.fillMaxWidth().height(260.dp)) {
            EmptyState(
                icon = Icons.Outlined.Park,
                title = stringResource(Res.string.showcase_empty_title),
                message = stringResource(Res.string.showcase_empty_message),
            )
        }
        Box(modifier = Modifier.fillMaxWidth().height(260.dp)) {
            ErrorState(
                message = stringResource(Res.string.showcase_error_message),
                onRetry = {},
            )
        }
        Box(modifier = Modifier.fillMaxWidth().height(260.dp)) {
            LoadingState()
        }
    }
}

/**
 * Visual QA grid for the species map-marker glyphs (issue #196): every glyph tinted
 * like it would be on a marker, so a broken/illegible silhouette is obvious at a
 * glance without spinning up the map.
 */
@Composable
private fun SpeciesGlyphsSection() {
    val painters = rememberSpeciesGlyphPainters()
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        SectionHeader(title = stringResource(Res.string.showcase_section_species_glyphs))
        painters.entries.chunked(4).forEach { rowEntries ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
            ) {
                rowEntries.forEach { (key, painter) ->
                    Column(
                        modifier = Modifier.weight(1f),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .aspectRatio(1f)
                                    .background(
                                        color = MaterialTheme.colorScheme.surfaceVariant,
                                        shape = RoundedCornerShape(FruxSpacing.sm),
                                    ),
                            contentAlignment = Alignment.Center,
                        ) {
                            Image(
                                painter = painter,
                                contentDescription = key,
                                modifier = Modifier.size(28.dp),
                                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.primary),
                            )
                        }
                        Text(
                            text = key,
                            style = MaterialTheme.typography.labelSmall,
                            fontSize = 10.sp,
                            textAlign = TextAlign.Center,
                        )
                    }
                }
                // Pad the last row so items keep a fixed 1/4 width instead of stretching.
                repeat(4 - rowEntries.size) {
                    Box(modifier = Modifier.weight(1f))
                }
            }
        }
    }
}
